import { Injectable } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormArray,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  Parameters,
  PledgeProductRead,
} from '@app/forms/subscription/definition/interface/Pledge';
import { FormConfig } from '@app/forms/subscription/definition/types/subscription';
import FormGroupArray from '@app/forms/subscription/services/FormGroupArray';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export abstract class EaSubscriptionBuilder extends PrFormAbstract {
  generateForm(entity: PledgeProductRead & { parameters: any }, option) {
    const parameters: Parameters = {
      ...option,
    };
    let options: AbstractControlOptions = {};

    const formConfig: FormConfig<PledgeProductRead & { parameters: any }> = {
      id: [entity.id],
      type: [entity.type],
      productId: [entity.productId],
      productName: [entity.productName],
      status: [entity.status],
      parameters: this.formBuilder.group({
        borrowerInsuranceContext: [
          entity?.parameters?.borrowerInsuranceContext
            ? entity.parameters.borrowerInsuranceContext
            : null,
        ],
        loan: this.formBuilder.group({
          insuranceQuotite: this.formBuilder.array(
            entity?.parameters?.loan?.insuranceQuotite &&
              entity?.parameters?.loan?.insuranceQuotite.length
              ? entity?.parameters?.loan?.insuranceQuotite.map(
                  insuranceQuotite =>
                    this.formBuilder.group({
                      ownerId: [
                        insuranceQuotite.ownerId != null
                          ? insuranceQuotite.ownerId
                          : null,
                        Validators.required,
                      ],
                      quotite: [
                        insuranceQuotite.quotite != null
                          ? insuranceQuotite.quotite * 100
                          : 100,
                        [
                          Validators.required,
                          Validators.max(100),
                          Validators.min(0),
                        ],
                      ],
                      assureLemoineDetails: [
                        insuranceQuotite.assureLemoineDetails
                          ? insuranceQuotite.assureLemoineDetails
                          : null,
                        Validators.required,
                      ],
                      repriseGroupe: [
                        !_.isNil(insuranceQuotite.repriseGroupe)
                          ? insuranceQuotite.repriseGroupe
                          : false,
                        Validators.required,
                      ],
                    }),
                )
              : this.generateDefaultInsuranceQuotite(entity),
          ),
        }),
      }),
    };

    return this.formBuilder.group(formConfig, options);
  }

  generateDefaultInsuranceQuotite(
    entity: PledgeProductRead & { parameters: any },
  ) {
    // Source des détenteurs : `owners` ({ ownerId, share }). On ne lit plus
    // `ownerIds` (déprécié). La quotité d'assurance reste un concept distinct
    // de la part de détention (`share`) : on ne mélange pas les deux valeurs.
    const loan = entity?.parameters?.loan;
    const ownerIds: number[] =
      loan?.owners && loan.owners.length
        ? loan.owners.map((o: any) => o.ownerId)
        : loan?.ownerIds && loan.ownerIds.length
          ? loan.ownerIds
          : [];

    if (ownerIds.length) {
      return ownerIds.map(ownerId =>
        this.formBuilder.group({
          ownerId: [ownerId, Validators.required],
          quotite: [
            100 / ownerIds.length,
            [Validators.required, Validators.max(100), Validators.min(0)],
          ],
          repriseGroupe: [false, Validators.required],
        }),
      );
    }

    return [
      this.formBuilder.group({
        ownerId: [null, Validators.required],
        quotite: [
          100,
          [Validators.required, Validators.max(100), Validators.min(0)],
        ],
        repriseGroupe: [false, Validators.required],
      }),
    ];
  }

  generate(entity: any, option) {
    return FormGroupArray.generate({
      entity,
      options: option,
      formBuilder: this.formBuilder,
      generateForm: this.generateForm.bind(this),
      propertyName: 'products',
    });
  }

  get products() {
    return this.form.get('products') as UntypedFormArray;
  }

  extract() {
    const products = _.cloneDeep(this.form.value.products);
    products.forEach(product => {
      product.parameters.loan.insuranceQuotite.forEach(insuranceQuotite => {
        insuranceQuotite.quotite = insuranceQuotite.quotite / 100;
        if (
          insuranceQuotite.assureLemoineDetails &&
          insuranceQuotite.assureLemoineDetails !== 'INF_EGAL_200K'
        ) {
          insuranceQuotite.repriseGroupe = false;
        }
      });
    });

    return products;
  }
}
